import { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { ErrorBoundary } from '@/components/optimization/ErrorBoundary';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import RedcapOdmFormImport from '@/pages/admin/RedcapOdmFormImport';
import RedcapOdmClinicalImport from '@/pages/admin/RedcapOdmClinicalImport';

// Lazy-loaded admin pages with retry for network reliability
const AdminCommandCenter = lazyWithRetry(() => import('@/pages/AdminCommandCenter'));
const UserDetails = lazyWithRetry(() => import('@/pages/UserDetails'));
const AdminSubscriptions = lazyWithRetry(() => import('@/pages/admin/AdminSubscriptions'));
const AdminSettings = lazyWithRetry(() => import('@/pages/admin/AdminSettings'));
const AdminPermissions = lazyWithRetry(() => import('@/pages/admin/AdminPermissions'));
const AdminBlogPosts = lazyWithRetry(() => import('@/pages/admin/AdminBlogPosts'));
const AdminRevenue = lazyWithRetry(() => import('@/pages/admin/AdminRevenue'));
const RevenueAnalyticsDashboard = lazyWithRetry(() => import('@/pages/admin/RevenueAnalyticsDashboard'));
const AdminLogs = lazyWithRetry(() => import('@/pages/admin/AdminLogs'));
const AdminCronJobs = lazyWithRetry(() => import('@/pages/admin/AdminCronJobs'));
const AdminPHCResyncMonitor = lazyWithRetry(() => import('@/pages/admin/AdminPHCResyncMonitor'));
const AdminModuleCountryAudit = lazyWithRetry(() => import('@/pages/admin/AdminModuleCountryAudit'));
const AdminTrainingCohorts = lazyWithRetry(() => import('@/pages/admin/AdminTrainingCohorts'));
const AdminEthicsCommittees = lazyWithRetry(() => import('@/pages/admin/AdminEthicsCommittees'));

const AdminLaunchReadiness = lazyWithRetry(() => import('@/pages/admin/AdminLaunchReadiness'));
const AdminForensicStatus = lazyWithRetry(() => import('@/pages/admin/AdminForensicStatus'));
const RareDiseaseSeedWave = lazyWithRetry(() => import('@/pages/admin/RareDiseaseSeedWave'));
const AdminREAIMDashboard = lazyWithRetry(() => import('@/pages/admin/AdminREAIMDashboard'));
const AdminNotifications = lazyWithRetry(() => import('@/pages/admin/AdminNotifications'));
const AdminVerifications = lazyWithRetry(() => import('@/pages/admin/AdminVerifications'));
const AdminRecruitmentQueue = lazyWithRetry(() => import('@/pages/admin/AdminRecruitmentQueue'));
const SecurityAuditDashboard = lazyWithRetry(() => import('@/pages/admin/SecurityAuditDashboard'));
const AdminAdvocacyPartners = lazyWithRetry(() => import('@/pages/admin/AdminAdvocacyPartners'));
const AdminDirectoryListings = lazyWithRetry(() => import('@/pages/admin/AdminDirectoryListings'));
const AdminClaimedListings = lazyWithRetry(() => import('@/pages/admin/AdminClaimedListings'));
const DirectoryOnboardingCommandCenter = lazyWithRetry(() => import('@/pages/admin/DirectoryOnboardingCommandCenter'));
const ProfileDedup = lazyWithRetry(() => import('@/pages/admin/ProfileDedup'));
const SubdomainUniqueness = lazyWithRetry(() => import('@/pages/admin/SubdomainUniqueness'));
const AdminProfileReminders = lazyWithRetry(() => import('@/pages/admin/AdminProfileReminders'));
const AdminLandingExperiments = lazyWithRetry(() => import('@/pages/admin/AdminLandingExperiments'));
const AdminEducationTemplates = lazyWithRetry(() => import('@/pages/admin/AdminEducationTemplates'));
const AdminBlog = lazyWithRetry(() => import('@/pages/AdminBlog'));
const AdminNews = lazyWithRetry(() => import('@/pages/admin/AdminNews'));
const AdminNowHiringDashboard = lazyWithRetry(() => import('@/pages/admin/AdminNowHiringDashboard'));
const AdminPatientOrders = lazyWithRetry(() => import('@/pages/admin/AdminPatientOrders'));
const AdminOrderWorkflow = lazyWithRetry(() => import('@/pages/admin/AdminOrderWorkflow'));
const SiteMarketingOutreach = lazyWithRetry(() => import('@/pages/admin/SiteMarketingOutreach'));
const CreateUser = lazyWithRetry(() => import('@/pages/admin/CreateUser'));
const CreateTeamMember = lazyWithRetry(() => import('@/pages/admin/CreateTeamMember'));
const ClientSetupDashboard = lazyWithRetry(() => import('@/pages/admin/ClientSetupDashboard'));
const SupportAccess = lazyWithRetry(() => import('@/pages/admin/SupportAccess'));
const AdminSitemap = lazyWithRetry(() => import('@/pages/admin/AdminSitemap'));
const SitemapHealthDashboard = lazyWithRetry(() => import('@/pages/admin/SitemapHealthDashboard'));
const AdminBulkAuth = lazyWithRetry(() => import('@/pages/admin/AdminBulkAuth'));
const AdminBulkUploadDashboard = lazyWithRetry(() => import('@/pages/admin/AdminBulkUploadDashboard'));
const AdminAIEnrichmentDashboard = lazyWithRetry(() => import('@/pages/admin/AdminAIEnrichmentDashboard'));
const EHRIntegrationStatus = lazyWithRetry(() => import('@/pages/admin/EHRIntegrationStatus'));
const BridgeOnePager = lazyWithRetry(() => import('@/pages/resources/BridgeOnePager'));
const AdminTrainingVideos = lazyWithRetry(() => import('@/pages/admin/AdminTrainingVideos'));
const AdminTrainingVideoManager = lazyWithRetry(() => import('@/pages/admin/AdminTrainingVideoManager'));
const AdminDirectoryEnrichment = lazyWithRetry(() => import('@/pages/admin/AdminDirectoryEnrichment'));
const AdminWebinars = lazyWithRetry(() => import('@/pages/admin/AdminWebinars'));
const CDMSAuditPage = lazyWithRetry(() => import('@/pages/admin/CDMSAuditPage'));
const AdminPHCConsultationAudit = lazyWithRetry(() => import('@/pages/admin/AdminPHCConsultationAudit'));
const AdminDHIS2Management = lazyWithRetry(() => import('@/pages/admin/AdminDHIS2Management'));
const AdminNRARegistry = lazyWithRetry(() => import('@/pages/admin/AdminNRARegistry'));
const GovTokensDashboard = lazyWithRetry(() => import('@/pages/admin/GovTokensDashboard'));
const AdminOrganizations = lazyWithRetry(() => import('@/pages/admin/AdminOrganizations'));
const AdminConsortiumLetters = lazyWithRetry(() => import('@/pages/admin/AdminConsortiumLetters'));
const DIEPICCosign = lazyWithRetry(() => import('@/pages/admin/DIEPICCosign'));
const AdminKenyaIDSRMatrix = lazyWithRetry(() => import('@/pages/admin/AdminKenyaIDSRMatrix'));
const AdminIDSRSubmissions = lazyWithRetry(() => import('@/pages/admin/AdminIDSRSubmissions'));
const AdminMEIndicators = lazyWithRetry(() => import('@/pages/admin/AdminMEIndicators'));
const AdminLIMSIDSRLinkages = lazyWithRetry(() => import('@/pages/admin/AdminLIMSIDSRLinkages'));
const AdminResidencyGrantGate = lazyWithRetry(() => import('@/pages/admin/AdminResidencyGrantGate'));




// AGENCY PROVISIONING SYSTEM
const ProvisionSite = lazyWithRetry(() => import('@/pages/admin/ProvisionSite'));
const AgencySitesDashboard = lazyWithRetry(() => import('@/pages/admin/AgencySitesDashboard'));
const SiteAdminDashboard = lazyWithRetry(() => import('@/pages/admin/SiteAdminDashboard'));
const SiteBillingDashboard = lazyWithRetry(() => import('@/pages/admin/SiteBillingDashboard'));
const SiteReadinessOverview = lazyWithRetry(() => import('@/pages/admin/SiteReadinessOverview'));
const FeasibilityBlast = lazyWithRetry(() => import('@/pages/admin/FeasibilityBlast'));
const FeasibilityDigest = lazyWithRetry(() => import('@/pages/admin/FeasibilityDigest'));
const FeasibilityMetrics = lazyWithRetry(() => import('@/pages/admin/FeasibilityMetrics'));
const FindAfricanMatches = lazyWithRetry(() => import('@/pages/sponsor/FindAfricanMatches'));
const AfricanTrialRegistry = lazyWithRetry(() => import('@/pages/admin/AfricanTrialRegistry'));
const OutreachDeliveryLog = lazyWithRetry(() => import('@/pages/admin/OutreachDeliveryLog'));
const FieldWorkerSyncLogPage = lazyWithRetry(() => import('@/pages/admin/FieldWorkerSyncLogPage'));
const AdminRedcapExport = lazyWithRetry(() => import('@/pages/admin/AdminRedcapExport'));
const SiteEditPage = lazyWithRetry(() => import('@/pages/admin/SiteEditPage'));
const SiteSubscriptionPage = lazyWithRetry(() => import('@/pages/admin/SiteSubscriptionPage'));
const SiteFeatureManagement = lazyWithRetry(() => import('@/pages/admin/SiteFeatureManagement'));

// Registry management pages
const SANCTRManagement = lazyWithRetry(() => import('@/pages/admin/SANCTRManagement'));
const PACTRManagement = lazyWithRetry(() => import('@/pages/admin/PACTRManagement'));
const ANZCTRManagement = lazyWithRetry(() => import('@/pages/admin/ANZCTRManagement'));
const ClinicalTrialsGovManagement = lazyWithRetry(() => import('@/pages/admin/ClinicalTrialsGovManagement'));
const EUCTISManagement = lazyWithRetry(() => import('@/pages/admin/EUCTISManagement'));
const ISRCTNManagement = lazyWithRetry(() => import('@/pages/admin/ISRCTNManagement'));
const WHOICTRPManagement = lazyWithRetry(() => import('@/pages/admin/WHOICTRPManagement'));
const UMINCTRManagement = lazyWithRetry(() => import('@/pages/admin/UMINCTRManagement'));
const ChiCTRManagement = lazyWithRetry(() => import('@/pages/admin/ChiCTRManagement'));
const CTRIManagement = lazyWithRetry(() => import('@/pages/admin/CTRIManagement'));
const IRCTManagement = lazyWithRetry(() => import('@/pages/admin/IRCTManagement'));
const DRKSManagement = lazyWithRetry(() => import('@/pages/admin/DRKSManagement'));

const SystemHealthLogs = lazyWithRetry(() => import('@/pages/admin/SystemHealthLogs'));
const OmopQualityDashboard = lazyWithRetry(() => import('@/pages/admin/OmopQualityDashboard'));
const WhatsAppDeliveryDashboard = lazyWithRetry(() => import('@/pages/admin/WhatsAppDeliveryDashboard'));
const RegistryHubDashboard = lazyWithRetry(() => import('@/pages/admin/RegistryHubDashboard'));
const RLSAuditDashboard = lazyWithRetry(() => import('@/pages/admin/RLSAuditDashboard'));
const AgreementsGenerator = lazyWithRetry(() => import('@/pages/admin/AgreementsGenerator'));
const AMRSurveillanceDashboard = lazyWithRetry(() => import('@/pages/admin/AMRSurveillanceDashboard'));

// Admin tools
const ResearchCenterImport = lazyWithRetry(() => import('@/pages/admin/ResearchCenterImport'));
const MatchingAnalyticsDashboard = lazyWithRetry(() => import('@/pages/admin/MatchingAnalyticsDashboard'));
const AfricanSitesScraper = lazyWithRetry(() => import('@/pages/admin/AfricanSitesScraper'));
const AdminOrganizationMerger = lazyWithRetry(() => import('@/pages/admin/AdminOrganizationMerger'));
const OfflineAudit = lazyWithRetry(() => import('@/pages/admin/OfflineAudit'));
const PassportCoverageAudit = lazyWithRetry(() => import('@/pages/admin/PassportCoverageAudit'));
const PassportReconciliation = lazyWithRetry(() => import('@/pages/admin/PassportReconciliation'));

const BrandingKit = lazyWithRetry(() => import('@/pages/admin/BrandingKit'));
const SponsorBillingDashboard = lazyWithRetry(() => import('@/pages/admin/SponsorBillingDashboard'));
const SponsorOutreachQueue = lazyWithRetry(() => import('@/pages/admin/SponsorOutreachQueue'));
const SponsorCRM = lazyWithRetry(() => import('@/pages/admin/SponsorCRM'));
const SponsorPipeline = lazyWithRetry(() => import('@/pages/admin/SponsorPipeline'));
const SponsorDeskInbox = lazyWithRetry(() => import('@/pages/admin/SponsorDeskInbox'));
const IISAdmissions = lazyWithRetry(() => import('@/pages/admin/IISAdmissions'));
const StaffEvidenceReadiness = lazyWithRetry(() => import('@/pages/admin/StaffEvidenceReadiness'));
const SponsorFunnelAnalytics = lazyWithRetry(() => import('@/pages/admin/SponsorFunnelAnalytics'));
const CountryComplianceDashboard = lazyWithRetry(() => import('@/pages/admin/CountryComplianceDashboard'));
const ContactOutreachHub = lazyWithRetry(() => import('@/pages/admin/ContactOutreachHub'));
const RegionalHealthDashboard = lazyWithRetry(() => import('@/pages/admin/RegionalHealthDashboard'));

// Patient Match Detail page
const PatientMatchDetail = lazyWithRetry(() => import('@/pages/admin/PatientMatchDetail'));
const SponsorDetail = lazyWithRetry(() => import('@/pages/admin/SponsorDetail'));
const AdminAcademy = lazyWithRetry(() => import('@/pages/admin/AdminAcademy'));
const AdminMalariaModelActivation = lazyWithRetry(() => import('@/pages/admin/AdminMalariaModelActivation'));
const AdminMalariaModelAuditLog = lazyWithRetry(() => import('@/pages/admin/AdminMalariaModelAuditLog'));
const AdminEFDASubmissionPack = lazyWithRetry(() => import('@/pages/admin/AdminEFDASubmissionPack'));
const AdminRegulatoryPackRequests = lazyWithRetry(() => import('@/pages/admin/AdminRegulatoryPackRequests'));
const SovereigntyCompliance = lazyWithRetry(() => import('@/pages/admin/SovereigntyCompliance'));
const AdminModuleActivation = lazyWithRetry(() => import('@/pages/admin/AdminModuleActivation'));

const EquivaWebhookEvents = lazyWithRetry(() => import('@/pages/admin/EquivaWebhookEvents'));
const BequivaSyncStatus = lazyWithRetry(() => import('@/pages/admin/BequivaSyncStatus'));


/**
 * Admin routes requiring admin role
 */
export const adminRoutes: RouteObject[] = [
  // Main admin command center (handles sub-routes internally)
  { 
    path: '/admin/command-center/*', 
    element: <ProtectedRoute requireAdmin><AdminCommandCenter /></ProtectedRoute> 
  },
  
  // AMR Surveillance Dashboard
  { path: '/admin/amr-surveillance', element: <ProtectedRoute requireAdmin><AMRSurveillanceDashboard /></ProtectedRoute> },

  // BRIDGE Academy admin
  { path: '/admin/academy', element: <ProtectedRoute requireAdmin><AdminAcademy /></ProtectedRoute> },

  // Plasmodium ONNX model activation
  { path: '/admin/ml/malaria-model', element: <ProtectedRoute requireAdmin><AdminMalariaModelActivation /></ProtectedRoute> },
  { path: '/admin/ml/malaria-model/audit', element: <ProtectedRoute requireAdmin><AdminMalariaModelAuditLog /></ProtectedRoute> },

  // EFDA / TMDA / NDA Platform Submission Pack — BRIDGE-wide SaMD / AI-MD portfolio
  { path: '/admin/regulatory/efda-submission-pack', element: <ProtectedRoute requireAdmin><AdminEFDASubmissionPack /></ProtectedRoute> },
  { path: '/admin/regulatory/submission-pack', element: <ProtectedRoute requireAdmin><AdminEFDASubmissionPack /></ProtectedRoute> },
  { path: '/admin/regulatory/pack-requests', element: <ProtectedRoute requireAdmin><AdminRegulatoryPackRequests /></ProtectedRoute> },
  { path: '/admin/consortium-letters', element: <ProtectedRoute requireAdmin><AdminConsortiumLetters /></ProtectedRoute> },
  { path: '/admin/di-epic-cosign', element: <ProtectedRoute requireAdmin><DIEPICCosign /></ProtectedRoute> },
  { path: '/admin/training-cohorts', element: <ProtectedRoute requireAdmin><AdminTrainingCohorts /></ProtectedRoute> },
  { path: '/admin/sovereignty-compliance', element: <ProtectedRoute requireAdmin><SovereigntyCompliance /></ProtectedRoute> },
  { path: '/admin/phc-resync-monitor', element: <ProtectedRoute requireAdmin><AdminPHCResyncMonitor /></ProtectedRoute> },
  { path: '/admin/module-country-audit', element: <ProtectedRoute requireAdmin><AdminModuleCountryAudit /></ProtectedRoute> },

  { path: '/admin/module-activation', element: <ProtectedRoute requireAdmin><AdminModuleActivation /></ProtectedRoute> },
  // Super-Admin governance mirrors — read-only. Primary home is /gov/:country/*
  { path: '/admin/kenya-idsr-matrix', element: <ProtectedRoute requireAdmin><AdminKenyaIDSRMatrix country="KE" readOnly /></ProtectedRoute> },
  { path: '/admin/idsr-submissions', element: <ProtectedRoute requireAdmin><AdminIDSRSubmissions country="KE" readOnly /></ProtectedRoute> },
  { path: '/admin/me-indicators', element: <ProtectedRoute requireAdmin><AdminMEIndicators country="KE" readOnly /></ProtectedRoute> },
  { path: '/admin/lims-idsr-linkages', element: <ProtectedRoute requireAdmin><AdminLIMSIDSRLinkages country="KE" readOnly /></ProtectedRoute> },
  { path: '/admin/residency-grant-gate', element: <ProtectedRoute requireAdmin><AdminResidencyGrantGate /></ProtectedRoute> },

  { path: '/admin/redcap-odm-import', element: <ProtectedRoute requireAdmin><RedcapOdmFormImport /></ProtectedRoute> },
  { path: '/admin/redcap-odm-data-import', element: <ProtectedRoute requireAdmin><RedcapOdmClinicalImport /></ProtectedRoute> },
  { path: '/admin/equiva-webhook-events', element: <ProtectedRoute requireAdmin><EquivaWebhookEvents /></ProtectedRoute> },
  { path: '/admin/bequiva-sync-status', element: <ProtectedRoute requireAdmin><BequivaSyncStatus /></ProtectedRoute> },

  { path: '/admin/rare-disease-seed-wave', element: <ProtectedRoute requireAdmin><RareDiseaseSeedWave /></ProtectedRoute> },
  { path: '/admin/profile-dedup', element: <ProtectedRoute requireAdmin><ProfileDedup /></ProtectedRoute> },
  { path: '/admin/subdomain-uniqueness', element: <ProtectedRoute requireAdmin><SubdomainUniqueness /></ProtectedRoute> },
  { path: '/admin/profile-reminders', element: <ProtectedRoute requireAdmin><AdminProfileReminders /></ProtectedRoute> },
  { path: '/admin/landing-experiments', element: <ProtectedRoute requireAdmin><AdminLandingExperiments /></ProtectedRoute> },
  { path: '/admin/education-templates', element: <ProtectedRoute requireAdmin><AdminEducationTemplates /></ProtectedRoute> },


  
  // Patient Match Detail Page (dedicated match view)
{
    path: '/admin/patient-match/:matchId',
    element: <ProtectedRoute requireAdmin><PatientMatchDetail /></ProtectedRoute>
  },
  
  // Sponsor Detail Page
  {
    path: '/admin/sponsor/:sponsorName',
    element: <ProtectedRoute requireAdmin><SponsorDetail /></ProtectedRoute>
  },
  
  // User management
  { path: '/admin/users/:userId', element: <ProtectedRoute requireAdmin><UserDetails /></ProtectedRoute> },
  { path: '/admin/users/:userId/edit', element: <ProtectedRoute requireAdmin><UserDetails /></ProtectedRoute> },
  { path: '/admin/users/create', element: <ProtectedRoute requireAdmin><CreateUser /></ProtectedRoute> },
  { path: '/admin/team/create', element: <ProtectedRoute requireAdmin><CreateTeamMember /></ProtectedRoute> },
  { path: '/admin/client-setup/:userId', element: <ProtectedRoute requireAdmin><ClientSetupDashboard /></ProtectedRoute> },
  { path: '/admin/support-access', element: <ProtectedRoute requireAdmin><SupportAccess /></ProtectedRoute> },
  
  // Content management
  { path: '/admin/blog', element: <ProtectedRoute requireAdmin><AdminBlog /></ProtectedRoute> },
  { path: '/admin/news', element: <ProtectedRoute requireAdmin><AdminNews /></ProtectedRoute> },
  { path: '/admin/blog-posts', element: <ProtectedRoute requireAdmin><AdminBlogPosts /></ProtectedRoute> },
  
  // System management
  { path: '/admin/subscriptions', element: <ProtectedRoute requireAdmin><AdminSubscriptions /></ProtectedRoute> },
  { path: '/admin/permissions', element: <ProtectedRoute requireAdmin><AdminPermissions /></ProtectedRoute> },
  { path: '/admin/revenue', element: <ProtectedRoute requireAdmin><AdminRevenue /></ProtectedRoute> },
  { path: '/admin/revenue-analytics', element: <ProtectedRoute requireAdmin><RevenueAnalyticsDashboard /></ProtectedRoute> },
  { path: '/admin/logs', element: <ProtectedRoute requireAdmin><AdminLogs /></ProtectedRoute> },
  { path: '/admin/cron-jobs', element: <ProtectedRoute requireAdmin><AdminCronJobs /></ProtectedRoute> },
  { path: '/admin/system-health', element: <ProtectedRoute requireAdmin><SystemHealthLogs /></ProtectedRoute> },
  { path: '/admin/omop-quality', element: <ProtectedRoute requireAdmin><OmopQualityDashboard /></ProtectedRoute> },
  { path: '/admin/whatsapp-delivery', element: <ProtectedRoute requireAdmin><WhatsAppDeliveryDashboard /></ProtectedRoute> },
  { path: '/admin/rls-audit', element: <ProtectedRoute requireAdmin><RLSAuditDashboard /></ProtectedRoute> },
  { path: '/admin/cdms-audit', element: <ProtectedRoute requireAdmin><CDMSAuditPage /></ProtectedRoute> },
  { path: '/admin/passport-coverage-audit', element: <ProtectedRoute requireAdmin><PassportCoverageAudit /></ProtectedRoute> },
  { path: '/admin/passport-reconciliation', element: <ProtectedRoute requireAdmin><PassportReconciliation /></ProtectedRoute> },
  { path: '/admin/phc-consultation-audit', element: <ProtectedRoute requireAdmin><AdminPHCConsultationAudit /></ProtectedRoute> },
  { path: '/admin/dhis2', element: <ProtectedRoute requireAdmin><AdminDHIS2Management /></ProtectedRoute> },
  { path: '/admin/agreements', element: <ProtectedRoute requireAdmin><AgreementsGenerator /></ProtectedRoute> },
  { path: '/admin/settings', element: <ProtectedRoute requireAdmin><AdminSettings /></ProtectedRoute> },
  { path: '/admin/security', element: <ProtectedRoute requireAdmin><SecurityAuditDashboard /></ProtectedRoute> },
  { path: '/admin/notifications', element: <ProtectedRoute requireAdmin><AdminNotifications /></ProtectedRoute> },
  { path: '/admin/verifications', element: <ProtectedRoute requireAdmin><AdminVerifications /></ProtectedRoute> },
  { path: '/admin/claimed-listings', element: <ProtectedRoute requireAdmin><AdminClaimedListings /></ProtectedRoute> },
  { path: '/admin/directory-onboarding-command-center', element: <ProtectedRoute requireAdmin><DirectoryOnboardingCommandCenter /></ProtectedRoute> },
  { path: '/admin/launch-readiness', element: <ProtectedRoute requireAdmin><AdminLaunchReadiness /></ProtectedRoute> },
  { path: '/admin/forensic-status', element: <ProtectedRoute requireAdmin><AdminForensicStatus /></ProtectedRoute> },
  { path: '/admin/re-aim', element: <ProtectedRoute requireAdmin><AdminREAIMDashboard /></ProtectedRoute> },
  { path: '/admin/sitemap', element: <ProtectedRoute requireAdmin><AdminSitemap /></ProtectedRoute> },
  { path: '/admin/sitemap-health', element: <ProtectedRoute requireAdmin><SitemapHealthDashboard /></ProtectedRoute> },
  
  // Trial registry management hub
  { path: '/admin/registry-hub', element: <ProtectedRoute requireAdmin><RegistryHubDashboard /></ProtectedRoute> },
  
  // Individual trial registry management pages (legacy routes - prefer Registry Hub)
  { path: '/admin/sanctr-trials', element: <ProtectedRoute requireAdmin><SANCTRManagement /></ProtectedRoute> },
  { path: '/admin/chictr-trials', element: <ProtectedRoute requireAdmin><ChiCTRManagement /></ProtectedRoute> },
  { path: '/admin/ctri-trials', element: <ProtectedRoute requireAdmin><CTRIManagement /></ProtectedRoute> },
  { path: '/admin/irct-trials', element: <ProtectedRoute requireAdmin><IRCTManagement /></ProtectedRoute> },
  { path: '/admin/drks-trials', element: <ProtectedRoute requireAdmin><DRKSManagement /></ProtectedRoute> },
  { path: '/admin/pactr-trials', element: <ProtectedRoute requireAdmin><PACTRManagement /></ProtectedRoute> },
  { path: '/admin/anzctr-trials', element: <ProtectedRoute requireAdmin><ANZCTRManagement /></ProtectedRoute> },
  { path: '/admin/clinicaltrials-gov-trials', element: <ProtectedRoute requireAdmin><ClinicalTrialsGovManagement /></ProtectedRoute> },
  { path: '/admin/eu-ctis-trials', element: <ProtectedRoute requireAdmin><EUCTISManagement /></ProtectedRoute> },
  { path: '/admin/isrctn-trials', element: <ProtectedRoute requireAdmin><ISRCTNManagement /></ProtectedRoute> },
  { path: '/admin/who-ictrp-trials', element: <ProtectedRoute requireAdmin><WHOICTRPManagement /></ProtectedRoute> },
  { path: '/admin/umin-ctr-trials', element: <ProtectedRoute requireAdmin><UMINCTRManagement /></ProtectedRoute> },
  
  // Orders & recruitment
  { path: '/admin/patients', element: <ProtectedRoute requireAdmin><AdminPatientOrders /></ProtectedRoute> },
  { path: '/admin/order-workflow', element: <ProtectedRoute requireAdmin><AdminOrderWorkflow /></ProtectedRoute> },
  { path: '/admin/recruitment', element: <ProtectedRoute requireAdmin><AdminRecruitmentQueue /></ProtectedRoute> },
  { path: '/admin/now-hiring', element: <ProtectedRoute requireAdmin><AdminNowHiringDashboard /></ProtectedRoute> },
  
  // Directory management - consolidated into Contact Hub
  { path: '/admin/advocacy', element: <ProtectedRoute requireAdmin><AdminAdvocacyPartners /></ProtectedRoute> },
  
  // Data import & analytics
  { path: '/admin/research-centers/import', element: <ProtectedRoute requireAdmin><ResearchCenterImport /></ProtectedRoute> },
  { path: '/admin/matching-analytics', element: <ProtectedRoute requireAdmin><MatchingAnalyticsDashboard /></ProtectedRoute> },
  { path: '/admin/ai-enrichment', element: <ProtectedRoute requireAdmin><AdminAIEnrichmentDashboard /></ProtectedRoute> },
  
  // EHR Integration
  { path: '/admin/ehr-integration-status', element: <ProtectedRoute requireAdmin><EHRIntegrationStatus /></ProtectedRoute> },
  
  // Marketing & Notifications
  { path: '/admin/notifications/marketing-outreach', element: <ProtectedRoute requireAdmin><SiteMarketingOutreach /></ProtectedRoute> },
  
  // Tools
  { path: '/admin/tools/bulk-auth', element: <ProtectedRoute requireAdmin><AdminBulkAuth /></ProtectedRoute> },
  { path: '/admin/bulk-upload', element: <ProtectedRoute requireAdmin><AdminBulkUploadDashboard /></ProtectedRoute> },
  { path: '/admin/tools/organization-merger', element: <ProtectedRoute requireAdmin><AdminOrganizationMerger /></ProtectedRoute> },
  { path: '/admin/branding-kit', element: <ProtectedRoute requireAdmin><BrandingKit /></ProtectedRoute> },
  
  
  // AGENCY PROVISIONING SYSTEM
  { path: '/admin/provision-site', element: <ProtectedRoute requireAdmin><ProvisionSite /></ProtectedRoute> },
  { path: '/admin/site-readiness', element: <ProtectedRoute requireAdmin><SiteReadinessOverview /></ProtectedRoute> },
  { path: '/admin/african-trial-registry', element: <ProtectedRoute requireAdmin><AfricanTrialRegistry /></ProtectedRoute> },
  { path: '/admin/feasibility-blast', element: <ProtectedRoute requireAdmin><FeasibilityBlast /></ProtectedRoute> },
  { path: '/admin/feasibility-digest', element: <ProtectedRoute requireAdmin><FeasibilityDigest /></ProtectedRoute> },
  { path: '/admin/feasibility-metrics', element: <ProtectedRoute requireAdmin><FeasibilityMetrics /></ProtectedRoute> },
  // Sponsor-facing copy of the digest (RPC itself allows sponsors + admins only)
  { path: '/sponsor/feasibility-digest', element: <ProtectedRoute><FeasibilityDigest /></ProtectedRoute> },
  { path: '/sponsor/find-african-matches', element: <ProtectedRoute><FindAfricanMatches /></ProtectedRoute> },
  { path: '/admin/outreach-delivery-log', element: <ProtectedRoute requireAdmin><OutreachDeliveryLog /></ProtectedRoute> },
  { path: '/admin/field-worker-sync-log', element: <ProtectedRoute requireAdmin><FieldWorkerSyncLogPage /></ProtectedRoute> },
  { path: '/admin/redcap-export', element: <ProtectedRoute requireAdmin><AdminRedcapExport /></ProtectedRoute> },
  { path: '/admin/sites', element: <ProtectedRoute requireAdmin><AgencySitesDashboard /></ProtectedRoute> },
  { path: '/admin/sites/:siteId/dashboard', element: <ProtectedRoute requireAdmin><SiteAdminDashboard /></ProtectedRoute> },
  { path: '/admin/sites/:siteId/billing', element: <ProtectedRoute requireAdmin><SiteBillingDashboard /></ProtectedRoute> },
  { path: '/admin/sites/:siteId/subscription', element: <ProtectedRoute requireAdmin><SiteSubscriptionPage /></ProtectedRoute> },
  { path: '/admin/sites/:siteId/edit', element: <ProtectedRoute requireAdmin><SiteEditPage /></ProtectedRoute> },
  { path: '/admin/sites/features', element: <ProtectedRoute requireAdmin><ErrorBoundary><SiteFeatureManagement /></ErrorBoundary></ProtectedRoute> },
  
  // NOTE: Site-specific operational routes (patient-management, billing, omop, diversity, conditions)
  // have been relocated to protected.routes.tsx where they architecturally belong.
  
  // SPONSOR BILLING SYSTEM
  { path: '/admin/sponsor-billing', element: <ProtectedRoute requireAdmin><SponsorBillingDashboard /></ProtectedRoute> },
  
  // SPONSOR CRM - Admin pipeline management for PTC monetization
  { path: '/admin/sponsor-crm', element: <ProtectedRoute requireAdmin><SponsorCRM /></ProtectedRoute> },

  // SPONSOR PIPELINE - deals, contacts, outreach stages and pipeline dashboard
  { path: '/admin/sponsor-pipeline', element: <ProtectedRoute requireAdmin><SponsorPipeline /></ProtectedRoute> },

  // SPONSOR DESK INBOX - verified-file and sponsor funnel requests
  { path: '/admin/sponsor-desk', element: <ProtectedRoute requireAdmin><SponsorDeskInbox /></ProtectedRoute> },
  { path: '/admin/iis-admissions', element: <ProtectedRoute requireAdmin><IISAdmissions /></ProtectedRoute> },

  // ETHICS COMMITTEES - add, edit and remove committees shown to trial sites
  { path: '/admin/ethics-committees', element: <ProtectedRoute requireAdmin><AdminEthicsCommittees /></ProtectedRoute> },

  // STAFF EVIDENCE READINESS - platform-wide roll-up of staff training and registration proof
  { path: '/admin/staff-evidence-readiness', element: <ProtectedRoute requireAdmin><StaffEvidenceReadiness /></ProtectedRoute> },

  // SPONSOR FUNNEL ANALYTICS - reach and drop-off across the offer ladder
  { path: '/admin/sponsor-funnel-analytics', element: <ProtectedRoute requireAdmin><SponsorFunnelAnalytics /></ProtectedRoute> },
  
  // SPONSOR OUTREACH QUEUE
  { path: '/admin/sponsor-outreach-queue', element: <ProtectedRoute requireAdmin><SponsorOutreachQueue /></ProtectedRoute> },
  
  // SPONSOR DATABASE VIEW - Redirect to unified Contact Hub
  { path: '/admin/sponsor-database', element: <ProtectedRoute requireAdmin><ContactOutreachHub /></ProtectedRoute> },
  
  // DIRECTORY LISTINGS - Redirect to unified Contact Hub  
  { path: '/admin/directory-listings', element: <ProtectedRoute requireAdmin><ContactOutreachHub /></ProtectedRoute> },
  
  // ONE PAGER
  { path: '/admin/one-pager', element: <ProtectedRoute requireAdmin><BridgeOnePager /></ProtectedRoute> },
  
  // COUNTRY COMPLIANCE DASHBOARD
  { path: '/admin/country-compliance', element: <ProtectedRoute requireAdmin><CountryComplianceDashboard /></ProtectedRoute> },
  
  // ADMIN TRAINING VIDEOS
  { path: '/admin/training-videos', element: <ProtectedRoute requireAdmin><AdminTrainingVideos /></ProtectedRoute> },
  { path: '/admin/training-video-manager', element: <ProtectedRoute requireAdmin><AdminTrainingVideoManager /></ProtectedRoute> },
  
  // DIRECTORY AI ENRICHMENT
  { path: '/admin/directory-enrichment', element: <ProtectedRoute requireAdmin><AdminDirectoryEnrichment /></ProtectedRoute> },
  
  // OFFLINE AUDIT DASHBOARD
  { path: '/admin/offline-audit', element: <ProtectedRoute requireAdmin><OfflineAudit /></ProtectedRoute> },
  
  // WEBINAR MANAGEMENT
  { path: '/admin/webinars', element: <ProtectedRoute requireAdmin><AdminWebinars /></ProtectedRoute> },
  
  // REGIONAL HEALTH INSIGHTS (Phase 3)
  { path: '/admin/regional-health', element: <ProtectedRoute requireAdmin><RegionalHealthDashboard /></ProtectedRoute> },

  // SOVEREIGN NRA REGISTRY (EDCTP3 RA Hub directory management)
  { path: '/admin/nra-registry', element: <ProtectedRoute requireAdmin><AdminNRARegistry /></ProtectedRoute> },

  // GOV DASHBOARD TOKENS (issue/revoke surveillance dashboard access)
  { path: '/admin/gov-tokens', element: <ProtectedRoute requireAdmin><GovTokensDashboard /></ProtectedRoute> },

  // ORGANIZATIONS MANAGEMENT (list + edit any org as platform admin)
  { path: '/admin/organizations', element: <ProtectedRoute requireAdmin><AdminOrganizations /></ProtectedRoute> },
];

// Export lazy components for prefetching
export const adminRoutePrefetch = {
  AdminCommandCenter,
  UserDetails,
  AdminSettings,
  AdminLogs,
  SecurityAuditDashboard,
};
